import React from "react";
import { Check, Clock3, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { SocialListeningRun, SocialListeningRunStatus } from "src/services/SocialListeningAPIs";
import CircularLoader from "src/components/reusable/CircularLoader/CircularLoader";
import { getNumberLocale } from "src/utils/numberFormat";
import { formatReportDate, getRunTitle } from "../Utils/HistoryUtils";

const HISTORY_KEY = "socialListingAgent.historyPage";

interface HistoryRowProps {
  run: SocialListeningRun;
  isSelected: boolean;
  isSelectable: boolean;
  onToggle: (_runId: number, _checked: boolean) => void;
  onView: () => void;
}

const StatusIcon: React.FC<{ status: SocialListeningRunStatus }> = ({ status }) => {
  const { t } = useTranslation();
  switch (status) {
    case "completed":
      return (
        <span className="history-status-success" aria-label={t(`${HISTORY_KEY}.completed`)}>
          <Check size={12} />
        </span>
      );
    case "partial":
      return (
        <span
          className="history-status-warning"
          title={t(`${HISTORY_KEY}.partial`, "Some brands could not be analysed")}
        >
          <TriangleAlert size={14} aria-hidden />
        </span>
      );
    case "failed":
      return (
        <span className="history-status-failed" title={t(`${HISTORY_KEY}.failed`, "Analysis failed")}>
          <span aria-hidden>!</span>
        </span>
      );
    default:
      return null;
  }
};

const HistoryRow: React.FC<HistoryRowProps> = ({ run, isSelected, isSelectable, onToggle, onView }) => {
  const { t, i18n } = useTranslation();
  const formattedDate = formatReportDate(
    run.completed_at ?? run.created_at,
    getNumberLocale(i18n.language),
    t(`${HISTORY_KEY}.at`)
  );
  const authorName = run.created_by?.name || t(`${HISTORY_KEY}.unknownAuthor`);
  const typeLabel =
    run.analyses_by ||
    t(
      run.analysis_type === "competitive"
        ? `${HISTORY_KEY}.analysisTypeCompetitor`
        : `${HISTORY_KEY}.analysisTypeBrand`
    );

  return (
    <div className="history-row">
      <input
        className="history-checkbox"
        type="checkbox"
        checked={isSelectable && isSelected}
        disabled={!isSelectable}
        onChange={event => onToggle(run.run_id, event.target.checked)}
      />
      <div>
        <div className="history-title-row">
          <span className="history-badge">{typeLabel}</span>
        </div>
        <div className="history-item-title-row">
          <span className="history-item-title">{getRunTitle(run)}</span>
          <StatusIcon status={run.status} />
        </div>
        <div className="history-author">
          {t(`${HISTORY_KEY}.byAuthor`, { author: authorName })}
        </div>
        {formattedDate && (
          <div className="history-date">
            <Clock3 size={12} />
            <span>{formattedDate}</span>
          </div>
        )}
      </div>

      <div className="history-status-cell">
        {run.status === "pending" && (
          <CircularLoader size={34} thickness={5} color="#36b25b" trackColor="#d9d9d9" />
        )}
      </div>

      <button type="button" className="button button--primary history-view-button" onClick={onView}>
        {t(`${HISTORY_KEY}.viewReport`)}
      </button>
    </div>
  );
};

export default HistoryRow;
